// Written Feel cues → playback spans, in the player's own clock.
//
// `compileCues` + the arrangement resolver work in exact rational QUARTER
// notes, which is right for the model: it is meter-independent and a 6/8 "and
// of 2" is exact. The player counts BEATS (denominator units) from the top of
// the flattened form. This module is the one place that conversion happens,
// so no scheduler has to do the arithmetic itself and get it subtly wrong.
//
// It works over the already-flattened playback form, which is what makes
// repeats free: `flattenChart` clones each measure per pass, so a cue in a
// first ending is simply present on the pass that plays it and absent on the
// one that doesn't — no re-anchoring, no occurrence bookkeeping.

import type { FeelCue } from '@/music/score/types';

/** A stretch of the performance under one written Feel. */
export interface FeelSpan {
  /** Absolute beat from the top of the flattened form. */
  fromBeat: number;
  /** The written label, or null before the first cue (the tune's default). */
  feel: string | null;
}

export interface FeelSpanMeasure {
  feel?: FeelCue;
}

/**
 * Spans of written feel over a flattened form.
 *
 * `barStartBeats[i]` is measure `i`'s absolute beat. A cue's rational
 * quarter-note offset converts to beats with the meter's denominator, so a
 * cue on beat 2 of a 6/8 bar lands where a reader would put it.
 *
 * Consecutive identical labels coalesce: a repeat that re-states "Latin"
 * every pass must not produce a boundary the drum scheduler would re-anchor
 * on. The first span always starts at beat 0 so the timeline is gapless.
 *
 * `pickupBeats` is the form's anacrusis. A cue on the first full bar governs
 * the lead-in too (see the return below) — nobody writes a feel on a pickup
 * bar, and treating it as its own unlabelled section is audible.
 */
export function feelSpans(
  measures: readonly FeelSpanMeasure[],
  barStartBeats: readonly number[],
  timeSignature: [number, number],
  pickupBeats = 0,
): FeelSpan[] {
  const beatsPerQuarter = timeSignature[1] / 4;
  const spans: FeelSpan[] = [{ fromBeat: 0, feel: null }];

  measures.forEach((measure, index) => {
    const label = measure.feel?.label?.trim();
    if (!label) return;
    const offset = measure.feel?.offset;
    const offsetBeats = offset && offset.den ? (offset.num / offset.den) * beatsPerQuarter : 0;
    const fromBeat = (barStartBeats[index] ?? 0) + offsetBeats;
    const previous = spans[spans.length - 1];
    if (previous.feel === label) return; // no boundary where nothing changes
    if (previous.fromBeat === fromBeat) previous.feel = label;
    else spans.push({ fromBeat, feel: label });
  });

  // A cue on the first full bar governs from beat 0, anacrusis included.
  //
  // The placeholder span exists for a tune that really does play a stretch
  // before its first written feel. A lead-in bar is not that: the pickup
  // belongs to the phrase it leads into, and a cue on bar 1 is the tune's
  // opening feel, not a change part-way through it. Left unabsorbed, the
  // lead-in resolves to whatever band happens to be loaded — which the
  // scheduler starts at bar 1's downbeat and then immediately crossfades
  // away from, heard as a burst of the wrong kit before the tune begins.
  const leadIn = spans[0];
  if (spans.length > 1 && leadIn.feel === null && spans[1].fromBeat <= pickupBeats + 1e-6) {
    const [first, ...rest] = spans.slice(1);
    return [{ ...first, fromBeat: 0 }, ...rest];
  }
  return spans;
}

/** The span covering `beat` (spans are ordered and gapless). */
export function feelAtBeat(spans: readonly FeelSpan[], beat: number): string | null {
  let current: string | null = null;
  for (const span of spans) {
    if (span.fromBeat > beat + 1e-6) break;
    current = span.feel;
  }
  return current;
}

/** Does the form carry any written cue at all? */
export function hasFeelCues(measures: readonly FeelSpanMeasure[]): boolean {
  return measures.some((m) => !!m.feel?.label?.trim());
}
